import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { supabase } from "@/integrations/supabase/client";
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { toast } from '@/components/ui/use-toast';
import { LessonFormFields } from '@/components/admin/lesson/LessonFormFields';
import { LessonFormData } from '@/components/admin/lesson/types';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Loader2 } from 'lucide-react';
import { updateLessonQuestions } from '@/utils/quizOperations';

const AdminCreateLesson: FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const form = useForm<LessonFormData>({
    defaultValues: {
      title: '',
      video_url: '',
      audio_url: '',
      content_text: '',
      level: 'principiante',
      published: true,
      is_premium: false,
      quiz_questions: []
    }
  });

  const onSubmit = async (data: LessonFormData) => {
    try {
      setIsLoading(true);
      
      // Validar campos requeridos
      if (!data.title?.trim()) {
        toast({
          title: "Error",
          description: "El título es obligatorio",
          variant: "destructive",
        });
        return;
      }

      if (!data.content_text?.trim()) {
        toast({
          title: "Error",
          description: "El contenido de la lección es obligatorio",
          variant: "destructive",
        });
        return;
      }

      // Validar preguntas del quiz
      if (!data.quiz_questions || data.quiz_questions.length !== 10) {
        toast({
          title: "Error",
          description: "Debes completar las 10 preguntas del quiz",
          variant: "destructive",
        });
        return;
      }

      // Validar que todas las preguntas estén completas
      const incompleteQuestion = data.quiz_questions.find(q => 
        !q.question?.trim() || 
        !q.correct_answer?.trim() || 
        !q.wrong_answers?.length === 3 || 
        q.wrong_answers.some(a => !a.trim())
      );

      if (incompleteQuestion) {
        toast({
          title: "Error",
          description: "Todas las preguntas deben tener una pregunta, una respuesta correcta y tres respuestas incorrectas",
          variant: "destructive",
        });
        return;
      }

      // Crear la lección
      const { data: lesson, error: lessonError } = await supabase
        .from('lessons')
        .insert([{
          title: data.title.trim(),
          content_text: data.content_text.trim(),
          video_url: data.video_url?.trim() || '',
          audio_url: data.audio_url?.trim() || '',
          level: data.level || 'beginner',
          published: true,
          is_premium: data.is_premium || false
        }])
        .select()
        .single();

      if (lessonError) throw lessonError;
      if (!lesson) throw new Error('No se pudo crear la lección');

      // Crear preguntas del quiz si existen
      if (data.quiz_questions?.length > 0) {
        await updateLessonQuestions(lesson.id, data.quiz_questions);
      }

      toast({
        title: "¡Éxito!",
        description: "Lección creada y publicada correctamente",
      });

      // Redirigir a la lista de lecciones en el panel de administración
      navigate('/admin/lessons');

    } catch (error: any) {
      console.error('Error creating lesson:', error);
      toast({
        title: "Error al crear la lección",
        description: error.message || "Hubo un problema al crear la lección",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Crear Nueva Lección</h1>
        </div>

        <Form {...form}>
          <form className="space-y-8">
            <LessonFormFields form={form} />
            
            <div className="flex justify-end space-x-4 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/lessons')}
              >
                Cancelar
              </Button>
              <Button 
                onClick={form.handleSubmit(onSubmit)}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creando...
                  </>
                ) : (
                  'Crear y Publicar'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </AdminLayout>
  );
};

export default AdminCreateLesson;